
import { useState, useCallback, useRef } from 'react';
import { useNodesState, useEdgesState, addEdge, Connection, Edge } from '@xyflow/react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getEdgeOptions } from '../EdgeOptions';

export const useOrganizationFlow = (isEditable: boolean, urlProjectId: string | null) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [showDeleteEdgeDialog, setShowDeleteEdgeDialog] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  const handleSaveToSupabase = async (currentNodes = nodes, currentEdges = edges, showAlert = false) => {
    if (!urlProjectId) {
      if (showAlert) {
        toast.error("Save Failed: No project selected");
      }
      return;
    }

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user.id) {
        if (showAlert) {
          toast.error("Save Failed: You must be logged in to save");
        }
        return;
      }

      const { data: projectData, error: projectError } = await supabase
        .from('project_requirements')
        .select('project_id, name')
        .eq('id', urlProjectId)
        .maybeSingle();

      if (projectError) {
        console.error('Error fetching project data:', projectError);
        throw projectError;
      }

      if (!projectData?.project_id) {
        throw new Error('No project_id found');
      }

      const project_id = projectData.project_id;
      const projectName = projectData.name || 'Untitled Project';

      const [{ error: nodesDeleteError }] = await Promise.all([
        supabase.from('nodes').delete().eq('project_id', project_id)
      ]);

      if (nodesDeleteError) throw nodesDeleteError;

      const nodePromises = currentNodes.map(node =>
        supabase.from('nodes').insert({
          project_id: project_id,
          user_id: session.session.user.id,
          node_id: node.id,
          node_type: node.data.type || 'roleNode',
          label: node.data.label,
          position_x: node.position.x,
          position_y: node.position.y,
          node_label_changed: node.data.node_label_changed || false,
          name: projectName,
          is_editable: typeof node.data.is_editable !== 'undefined' ? node.data.is_editable : isEditable
        })
      );
      await Promise.all(nodePromises);

      const roleJdPromises = currentNodes.map(async node => {
        const { data: existingRole } = await supabase
          .from('role_jd')
          .select('id')
          .eq('project_id', project_id)
          .eq('role_title', node.data.label)
          .maybeSingle();

        if (!existingRole) {
          return supabase.from('role_jd').insert({
            project_id: project_id,
            user_id: session.session.user.id,
            role_title: node.data.label,
            role_description: {},
            name: projectName
          });
        }
        return Promise.resolve();
      });
      await Promise.all(roleJdPromises);

      const serializedNodes = JSON.parse(JSON.stringify(currentNodes));
      const serializedEdges = JSON.parse(JSON.stringify(currentEdges));

      const { data: existingStructure } = await supabase
        .from('organization_structures')
        .select('*')
        .eq('project_id', project_id)
        .maybeSingle();

      if (existingStructure) {
        await supabase
          .from('organization_structures')
          .update({
            nodes: serializedNodes,
            edges: serializedEdges,
            updated_at: new Date().toISOString(),
            is_saved: true,
            is_editable: isEditable,
            name: projectName
          })
          .eq('project_id', project_id);
      } else {
        await supabase.from('organization_structures').insert({
          nodes: serializedNodes,
          edges: serializedEdges,
          updated_at: new Date().toISOString(),
          is_saved: true,
          is_editable: isEditable,
          project_id: project_id,
          user_id: session.session.user.id,
          name: projectName
        });
      }

      if (showAlert) {
        toast.success("Organization structure saved successfully");
      }
    } catch (error) {
      console.error('Error saving organization structure:', error);
      if (showAlert) {
        toast.error("Failed to save organization structure");
      }
    }
  };

  const onConnect = useCallback((params: Connection) => {
    if (!isEditable) {
      toast.error("This organization structure cannot be edited");
      return;
    }
    
    const edgeOptions = getEdgeOptions(params.source || '', params.target || '');
    const newEdge = {
      ...params,
      ...edgeOptions
    };
    setEdges(eds => {
      const updatedEdges = addEdge(newEdge, eds);

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        handleSaveToSupabase(nodes, updatedEdges, false);
      }, 100);
      return updatedEdges;
    });
  }, [nodes, setEdges, isEditable]);

  const onEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.preventDefault();
    
    if (!isEditable) {
      toast.error("This organization structure cannot be edited");
      return;
    }
    
    setSelectedEdgeId(edge.id);
    setShowDeleteEdgeDialog(true);
  }, [isEditable]);

  const handleDeleteEdge = useCallback(() => {
    if (selectedEdgeId) {
      setEdges(eds => {
        const updatedEdges = eds.filter(e => e.id !== selectedEdgeId);

        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
          handleSaveToSupabase(nodes, updatedEdges, false);
        }, 100);
        return updatedEdges;
      });
    }
    setShowDeleteEdgeDialog(false);
    setSelectedEdgeId(null);
  }, [selectedEdgeId, nodes, setEdges]);

  const handleCancelEdgeDelete = useCallback(() => {
    if (selectedEdgeId) {
      setEdges(eds => eds.map(edge => ({
        ...edge,
        selected: edge.id === selectedEdgeId ? false : edge.selected,
        style: {
          ...edge.style,
          stroke: edge.id === selectedEdgeId ? '#ffffff' : edge.style?.stroke
        }
      })));
    }
    setShowDeleteEdgeDialog(false);
    setSelectedEdgeId(null);
  }, [selectedEdgeId, setEdges]);

  return {
    nodes,
    setNodes,
    onNodesChange,
    edges,
    setEdges,
    onEdgesChange,
    onConnect,
    onEdgeClick,
    selectedEdgeId,
    showDeleteEdgeDialog,
    setShowDeleteEdgeDialog,
    handleDeleteEdge,
    handleCancelEdgeDelete,
    handleSaveToSupabase,
    saveTimeoutRef
  };
};
